import { ReactNode, useEffect, useMemo } from "react";
import { Provider } from "react-redux";

import { store } from "./store";
import { useAppSelector } from "./hooks";
import { baseTheme, hexToRgba, shadeHexColor } from "../assets/theme";
import { ToastHost } from "../components/ToastHost";
import { I18nProvider } from "../features/i18n/I18nProvider";
import { LinkViewerHost } from "../components/links/LinkViewer";

const ThemeSync = ({ children }: { children: ReactNode }) => {
  const tenantTheme = useAppSelector((state) => state.tenant.branding);

  const tokens = useMemo(() => {
    const primary = tenantTheme?.theme_primary || baseTheme.colors.primary;
    const accent = tenantTheme?.theme_accent || baseTheme.colors.accent;
    return {
      "--color-bg": baseTheme.colors.background,
      "--color-surface": baseTheme.colors.surface,
      "--color-text": baseTheme.colors.text,
      "--color-primary": primary,
      "--color-accent": accent,
      "--color-accent-strong": shadeHexColor(accent, -18),
      "--color-accent-soft": hexToRgba(accent, 0.12),
      "--color-muted": baseTheme.colors.muted,
      "--color-border": baseTheme.colors.border,
      "--color-success": baseTheme.colors.success,
      "--color-danger": baseTheme.colors.danger,
    };
  }, [tenantTheme]);

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(tokens).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [tokens]);

  return <>{children}</>;
};

export const AppProviders = ({ children }: { children: ReactNode }) => (
  <Provider store={store}>
    {/* Inside Provider (it may read auth state later) but outside ThemeSync's
        children so every screen, including the toaster, is translated. */}
    <I18nProvider>
      <ThemeSync>
        {/* One listener for every answer link in the app, opened in place. */}
        <LinkViewerHost>{children}</LinkViewerHost>
        <ToastHost />
      </ThemeSync>
    </I18nProvider>
  </Provider>
);
